<template>
  <div>
    <yv-index
      ref="progressReport"
      :bulkActions="tableBulkActions"
      :columns="columns"
      :enableExport="false"
      :formSearch="formSearch"
      :formatTableData="formatTableData"
      :path="path"
      :sort-data="sortData"
      :sortable="sortable"
      :tableActionsComponent="TableActions"
      mainField="username"
      selectable="multiple"
    >
      <template #search-actions="{ actions }">
        <div class="flex justify-center">
          <md-button
            class="md-info md-round"
            native-type="submit"
            @click.native.prevent="actions.search"
            >{{ $t("common.search") }}
          </md-button>
          <md-button
            v-if="isInputter"
            class="md-warning md-round ml-3"
            @click="$modal.show(addProgressReportModal.name)"
            >{{ $t("common.add") }}
          </md-button>
        </div>
      </template>
    </yv-index>

    <common-modal
      :name="addProgressReportModal.name"
      :primary-action="addProgressReportModal.primaryAction"
      :secondary-actions="addProgressReportModal.secondaryActions"
      :title="addProgressReportModal.title"
      height="auto"
      width="80%"
    >
      <template #modal-body>
        <add-progress-report ref="addProgressReport"></add-progress-report>
      </template>
    </common-modal>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { YvIndex } from "@/components/Base";
import { service_names, service_paths } from "@/constants/Services";
import { capitalizeFirstLetter, getId, Message } from "@/helpers/utils";
import { DEFAULT_OPTION_ALL } from "@/constants";
import { statusSelect } from "../Components/utils";
import TableActions from "./Components/TableActions";
import AddProgressReport from "./AddProgressReport";
import {
  generateStatusReport,
  generateTypeReport
} from "../../../helpers/text-helper";
import debounce from "lodash/debounce";
import { mapGetters } from "vuex";
import { ReportTypes } from "../../../constants/Report";

export default {
  name: "progress-report",
  components: { YvIndex, AddProgressReport },
  data() {
    return {
      path: `${service_paths.progress}/program-id`,
      programOptSelected: [],
      classOptSelected: [],
      studentOptSelected: [],
      currentSearchKeyClass: "",
      formSearch: [
        {
          label: "common.program",
          name: "program_ids",
          value: [],
          type: "checkbox-multi-select",
          options: [],
          trackBy: "id",
          displayField: "name",
          filtering: this.filterProgram,
          width: 30
        },
        {
          label: "common.class",
          name: "class_ids",
          value: [],
          type: "checkbox-multi-select",
          options: [],
          trackBy: "id",
          displayField: "name",
          filtering: this.filterClass,
          width: 30
        },
        {
          label: "smi_sri.search_by_full_name",
          name: "student_ids",
          value: [],
          type: "checkbox-multi-select",
          options: [],
          trackBy: "id",
          displayField: "name",
          filtering: this.filterStudent,
          width: 30
        },
        {
          label: "common.type",
          name: "report_type",
          value: "",
          type: "select",
          options: [
            ...DEFAULT_OPTION_ALL,
            { id: "monthly_report", name: "submitted_report.monthly_report" },
            { id: ReportTypes.MidTerm, name: "submitted_report.mid_term" },
            {
              id: ReportTypes.EndOfOne,
              name: "submitted_report.end_of_semester_1"
            },
            {
              id: ReportTypes.EndOfTwo,
              name: "submitted_report.end_of_semester_2"
            }
          ],
          width: 30
        },
        {
          label: "progress_report.template_report",
          name: "report_template",
          value: "",
          type: "select",
          options: [
            ...DEFAULT_OPTION_ALL,
            // { id: "default", name: "survey.default" },
            { id: "standard", name: "program.standard" },
            { id: "honors", name: "subject.honors" },
            { id: "high_school", name: "program.high_school" }
            // { id: "igs-prep", name: "Prep" }
          ],
          width: 30
        },
        {
          label: "common.status",
          name: "status",
          value: "",
          type: "select",
          options: statusSelect,
          width: 30
        }
      ],
      columns: {
        avatar_url: "class.student_col_avatar",
        student_code: "common.student_code",
        full_name: "common.full_name",
        title: "setting.title",
        report_type: "common.type",
        report_template: "progress_report.template_report",
        status: "common.status",
        type_sending_model: "progress_report.review_type"
      },
      sortable: [
        "full_name",
        "title",
        "report_type",
        "report_template",
        "status",
        "type_sending_model"
      ],
      sortData: {
        currentSort: "created_at",
        currentSortOrder: "desc"
      },
      tableBulkActions: [
        {
          content: this.$t("common.delete"),
          action: () => {
            this.confirmDeletePR();
          }
        },
        {
          content: "Refresh score",
          action: () => {
            this.confirmRefreshScore();
          }
        }
      ],
      TableActions,
      addProgressReportModal: {
        name: "progress-report",
        title: this.$t("progress_report.add"),
        primaryAction: {
          title: "common.add",
          action: this.doSubmit
        },
        secondaryActions: [
          {
            title: "common.cancel",
            action: this.closeModal
          }
        ]
      }
    };
  },
  computed: {
    ...mapGetters("user", ["info"]),
    isInputter() {
      return this.info?.inputterOrReviewDetail?.authorized === "Inputter";
    },
    programIdsSelected() {
      return this.formSearch[0].value;
    },
    classIdsSelected() {
      return this.formSearch[1].value;
    },
    studentIdsSelect() {
      return this.formSearch[2].value;
    },
    paramsGetStudent() {
      return {
        program_ids: this.programIdsSelected,
        class_ids: this.classIdsSelected
      };
    }
  },
  watch: {
    programIdsSelected(newProgram) {
      this.programOptSelected = this.formSearch[0].options.filter(program =>
        newProgram.includes(program.id)
      );
      this.getProgramList();
      this.getStudentList();
      this.formSearch[2].value = [];
    },
    classIdsSelected(newClass) {
      this.classOptSelected = this.formSearch[1].options.filter(classItem =>
        newClass.includes(classItem.id)
      );
      this.getClassList();
      this.getStudentList();
      this.formSearch[2].value = [];
    },
    studentIdsSelect(newStudent) {
      this.studentOptSelected = this.formSearch[2].options.filter(student =>
        newStudent.includes(student.id)
      );
      this.getStudentList();
    }
  },
  methods: {
    filterProgram(event) {
      this.debounceProgram(event);
    },

    debounceProgram: debounce(function(searchKey) {
      this.getProgramList(searchKey);
    }, 300),

    getProgramList(searchKey = "") {
      this.$axios
        .get(`${service_paths.programs}`, {
          "name__~": searchKey,
          limit: 100
        })
        .then(({ data }) => {
          if (data) {
            let newOptions = data.data.filter(
              item => !this.programIdsSelected.includes(item.id)
            );
            this.formSearch[0].options = [
              ...this.programOptSelected,
              ...newOptions
            ];
          }
        });
    },

    filterClass(event) {
      this.debounceClass(event);
    },

    debounceClass: debounce(function(searchKey) {
      this.getClassList(searchKey);
    }, 500),

    getClassList(searchKey = "") {
      this.currentSearchKeyClass = searchKey;
      this.$axios
        .get(
          `${service_paths.classes}`,
          {
            "name__~": searchKey,
            limit: 50
          },
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(({ data }) => {
          if (this.currentSearchKeyClass !== searchKey) return;
          let newOptions = data.data.filter(
            item => !this.classIdsSelected.includes(item.id)
          );
          this.formSearch[1].options = [
            ...this.classOptSelected,
            ...newOptions
          ];
        });
    },

    // get student by student_id/full name/email or class_id or program_id
    filterStudent(event) {
      this.debounceStudent(event);
    },

    debounceStudent: debounce(function(searchKey) {
      this.getStudentList(searchKey);
    }, 500),

    getStudentList(searchKey = "") {
      this.$axios
        .get(
          `${service_names.user}/progress-setting/students/staff`,
          {
            limit: 100,
            searchKey: searchKey,
            ...this.paramsGetStudent
          },
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(({ data }) => {
          let allOption = [];
          data.data.map(student => {
            allOption.push({
              id: getId(student),
              name: student.student_code + " - " + student.full_name
            });
          });

          let newOptions = allOption.filter(
            item => !this.studentIdsSelect.includes(item.id)
          );
          this.formSearch[2].options = [
            ...this.studentOptSelected,
            ...newOptions
          ];
        });
    },

    formatTableData(tableData) {
      return tableData.map(reportItem => {
        const avatar_url =
          reportItem.student.avatar_url || `/img/avt_placeholder.jpg`;
        reportItem.avatar_url = `<img src="${avatar_url}" class="object-cover rounded-full w-[42px] h-[42px]">`;
        reportItem.student_code = reportItem.student.student_code;
        reportItem.full_name = reportItem.student.full_name;
        reportItem.title = capitalizeFirstLetter(reportItem.title);
        reportItem.report_type = generateTypeReport(reportItem.report_type);
        reportItem.report_template = generateTypeReport(
          reportItem.report_template
        );
        reportItem.status = generateStatusReport(reportItem.status);

        return reportItem;
      });
    },

    confirmDeletePR() {
      Swal.fire({
        title: this.$t("common.confirmation"),
        text: this.$t("progress_report.confirm_delete"),
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-danger btn-fill",
        cancelButtonClass: "md-button md-secondary btn-fill",
        confirmButtonText: this.$t("common.delete"),
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          const progress_ids = this.$refs.progressReport.selectedItems.map(
            item => getId(item)
          );
          this.$axios
            .delete(`${service_paths.progress}/delete-all`, { progress_ids })
            .then(() => {
              Message.success(this.$t("common.delete_success"));

              // Reload table data
              this.$refs.progressReport.getTableData();
            });
        }
      });
    },

    confirmRefreshScore() {
      Swal.fire({
        title: this.$t("common.confirmation"),
        text: this.$t("progress_report.confirm_refresh"),
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-danger btn-fill",
        cancelButtonClass: "md-button md-secondary btn-fill",
        confirmButtonText: this.$t("common.ok"),
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          const progress_report_ids = this.$refs.progressReport.selectedItems.map(
            item => getId(item)
          );
          this.$axios
            .post(`${service_paths.progress}/report/update-score`, {
              progress_report_ids
            })
            .then(() => {
              Message.success("Refreshed!");

              // Reload table data
              this.$refs.progressReport.getTableData();
            });
        }
      });
    },

    doSubmit() {
      this.$refs.addProgressReport.$refs.addForm.submitForm();
    },

    addSuccessPR() {
      this.$refs.progressReport.getTableData();
      this.closeModal();
    },

    closeModal() {
      this.$modal.hide(this.addProgressReportModal.name);
    }
  },
  created() {
    this.getProgramList();
    this.getClassList();
    this.getStudentList();
  },
  provide() {
    return {
      addSuccessPR: this.addSuccessPR,
      confirmDeletePR: this.confirmDeletePR
    };
  }
};
</script>
